import {
  INSPECTION_STATUSES,
  LEAK_TEST,
  MODEL_ATTRIBUTES,
  JUDGEMENT,
  JUDGEMENT_LABEL,
  VARIABLE_HARDNESS,
  NOT_EXIST_RESULT_TEXT,
  DTA_IMAGE_SIZE,
} from '../_constants/inspectionConst';
import { JUDGEMENT_LABEL_COLOR } from '../_constants/styleConst';
import {
  CategoryResultTable,
  ItemResultTable,
  TypeResultTable,
} from '../_common/Report/ResultTable';
import {
  CategoryResult,
  GetInspectionItem,
  GetQcType,
  GetRepairRequest,
  GetReport,
  GetReportFormat,
  InspectionType,
  ItemResult,
} from '../services/apiTypes';
import {
  EXFM_PATH,
  EXFM_QUERY_PARAM,
  INSPEX_PATHNAME,
} from '../_constants/Const';
import { ConfirmParts } from '../pages/PartSelection/components/Modal/ConfirmPartsModal';

/** 診断結果が(空)か */
export function isEmpty(judgement: number) {
  return judgement === JUDGEMENT.EMPTY;
}

/** 診断結果がOKか */
export function isOk(judgement: number) {
  return judgement === JUDGEMENT.OK;
}

/** 診断結果がNGか */
export function isNg(judgement: number) {
  return judgement === JUDGEMENT.NG;
}

/** 診断結果がN/Aか */
export function isNa(judgement: number) {
  return judgement === JUDGEMENT.NA;
}

/** 中項目の診断が完了しているか （すべて空欄の確認を含まれていない）*/
export function isCompleteInspectionOfType(itemDataList: GetInspectionItem[]) {
  return itemDataList.every((depth0) => {
    // 階層0に空があれば未完了
    if (isEmpty(depth0.judgement)) {
      return false;
    }
    // NGの項目があれば、階層1の結果を確認
    if (isNg(depth0.judgement) && depth0.itemData.length > 0) {
      return (
        depth0.itemData.every((depth1) => {
          // NGの項目があれば、階層2の結果を確認
          if (isNg(depth1.judgement) && depth1.itemData.length > 0) {
            return (
              depth1.itemData.every((depth2) => !isEmpty(depth2.judgement)) &&
              depth1.itemData.some((depth2) => isNg(depth2.judgement))
            );
          }
          return !isEmpty(depth1.judgement);
        }) && depth0.itemData.some((depth1) => isNg(depth1.judgement))
      );
    }
    return true;
  });
}

/** 全て空欄を確認 */
export function isAllBlankInspectionOfType(itemDataList: GetInspectionItem[]) {
  return itemDataList.every((item) => {
    if (!isEmpty(item.judgement)) {
      return false;
    }
    return item.itemData.every((item) => isEmpty(item.judgement));
  });
}

/** 中項目の診断が完了しているか */
export function isComplete(status: 0 | 1 | 2) {
  return status === 2;
}

/** 中項目の診断が入力不備か */
export function isUnfilled(status: 0 | 1 | 2) {
  return status === 1;
}

/** ステータスを取得 */
export function getStatusInspectionOfType(itemDataList: GetInspectionItem[]) {
  return isAllBlankInspectionOfType(itemDataList)
    ? 0
    : isCompleteInspectionOfType(itemDataList)
      ? 2
      : 1;
}

/** 進捗率を計算する */
export function calcCategoryProgress(completeNum: number, typeLength: number) {
  return Math.floor((completeNum / typeLength) * 100);
}

/**
 * 中項目、小分類ID文字列から末尾のindexをnumberで取得
 * @param typeId
 * @returns  index
 * ex) '2-4' => 4
 * ex) '3-2-1' => 1
 */
export function getIdIndex(typeId: string) {
  const idList = typeId.split('-');
  return Number(idList[idList.length - 1]);
}

/** 判定基準のテキストがあるか */
export function checkHasCriteriaText(itemDataList: GetInspectionItem[]) {
  return itemDataList.some((item) => item.criteria.text !== '');
}

/** 問題箇所が存在するか */
export function checkHasDetail(itemDataList: GetInspectionItem[]) {
  return itemDataList.some((item) =>
    item.itemData.some((problem) => problem.itemData.length > 0),
  );
}

/** マトリクスの問題箇所が存在するか */
export function checkHasDetailMatrix(problemList: GetInspectionItem[]) {
  return problemList.some((problem) => problem.itemData.length > 0);
}

/** 判定結果に応じて色を取得 */
export function getColorByJudgement(judgement: number | undefined) {
  try {
    if (!judgement) {
      return '';
    }

    if (isNg(judgement)) {
      return JUDGEMENT_LABEL_COLOR.NG;
    } else if (isOk(judgement)) {
      return JUDGEMENT_LABEL_COLOR.OK;
    } else if (isNa(judgement)) {
      return JUDGEMENT_LABEL_COLOR.NA;
    } else {
      return '';
    }
  } catch {
    return '';
  }
}

/** 結果の文字に応じて色を取得 */
export function getColorByLabel(judgementLabel: string) {
  switch (judgementLabel) {
    case 'OK':
      return JUDGEMENT_LABEL_COLOR.OK;
    case 'NG':
      return JUDGEMENT_LABEL_COLOR.NG;
    case 'N/A':
      return JUDGEMENT_LABEL_COLOR.NA;
    default:
      return '';
  }
}

/** 機種の属性から先端部の画像を判定する */
export function getDtaImage(repairRequest: GetRepairRequest | undefined) {
  if (!repairRequest || !repairRequest.modelInfo) {
    return `DTA_1`;
  }
  const modelInfo = [
    repairRequest.modelInfo.attributeId1,
    repairRequest.modelInfo.attributeId2,
    repairRequest.modelInfo.attributeId3,
    repairRequest.modelInfo.attributeId5,
    repairRequest.modelInfo.attributeId6,
  ];
  // 画像の種類を判定 優先度 2>3>4>6>5>1
  if (modelInfo.includes(MODEL_ATTRIBUTES.EUS)) {
    return `DTA_2`;
  }
  if (modelInfo.includes(MODEL_ATTRIBUTES.DUODENUM)) {
    return `DTA_3`;
  }
  if (modelInfo.includes(MODEL_ATTRIBUTES.LARGE_FORCEPS_2CH)) {
    return `DTA_4`;
  }
  if (modelInfo.includes(MODEL_ATTRIBUTES.EBUS)) {
    return `DTA_6`;
  }
  if (
    modelInfo.includes(MODEL_ATTRIBUTES.SLIM) ||
    modelInfo.includes(MODEL_ATTRIBUTES.G7_SLIM)
  ) {
    return `DTA_5`;
  }
  return `DTA_1`;
}

/** 気密検査の入力チェック */
export function isInputAutoLeakTest(itemData: GetInspectionItem[]) {
  const { dryLeakTestItemId, wetLeakTestItemId } = LEAK_TEST;
  let isInput = true;
  let isAuto = false;
  for (const item of itemData) {
    switch (item.id) {
      case dryLeakTestItemId:
        isAuto = isOk(item.judgement);
        break;
      case wetLeakTestItemId:
        if (isNg(item.judgement) && item.itemData.length > 0) {
          // 水没気密検査がNGで画像未選択の場合は未入力とする
          isInput = item.itemData.some(({ judgement }) => isNg(judgement));
          isAuto = false;
        } else if (isOk(item.judgement)) {
          isAuto = true;
        }
        break;
      default:
        isInput = !isEmpty(item.judgement);
        break;
    }
    if (!isInput) {
      // 未入力項目があれば以降は処理しない
      break;
    }
  }
  return isInput && isAuto;
}

/** RETURN REPAIR時の上書き処理(中項目1つ分) */
export function convertQcTypeDataToInspectionTypeData(
  data: GetQcType,
): InspectionType {
  const { before, after } = data;
  /** 上書き後の階層0のリスト */
  const convertedDepth0List = after.itemData.map((afterD0) => {
    const beforeD0 = before.itemData.find((b0) => b0.id === afterD0.id);
    // 前回値が無ければ今回の結果をそのまま登録
    if (beforeD0 === undefined) {
      return afterD0;
    }
    /** 上書き後の階層1のリスト */
    const convertedDepth1List =
      afterD0.itemData.length === 0
        ? []
        : afterD0.itemData.map((afterD1) => {
            const beforeD1 = beforeD0.itemData.find(
              (b1) => b1.id === afterD1.id,
            );
            // 前回値が無ければ今回の結果をそのまま登録
            if (beforeD1 === undefined) {
              return afterD1;
            }
            /** 上書き後の階層2のリスト */
            const convertedDepth2List =
              afterD1.itemData.length === 0
                ? []
                : afterD1.itemData.map((afterD2) => {
                    const beforeDepth2 = beforeD1.itemData.find(
                      (b2) => b2.id === afterD2.id,
                    );
                    // 前回値が無ければ今回の結果をそのまま登録
                    if (beforeDepth2 === undefined) {
                      return afterD2;
                    }

                    return {
                      ...afterD2,
                      judgement: convertJudgement(
                        beforeDepth2.judgement,
                        afterD2.judgement,
                      ),
                    };
                  });

            return {
              ...afterD1,
              itemData: convertedDepth2List,
              judgement: convertJudgement(
                beforeD1.judgement,
                afterD1.judgement,
              ),
              // 硬度可変測定は階層1に数値を持つため上書きしておく
              numericalValue: getNumericalValue(beforeD1, afterD1),
            };
          });

    return {
      ...afterD0,
      itemData: convertedDepth1List,
      judgement: convertDepth0Judgement(
        beforeD0.judgement,
        afterD0.judgement,
        convertedDepth1List,
      ),
      numericalValue: getNumericalValue(beforeD0, afterD0),
    };
  });
  return {
    ...after,
    itemData: convertedDepth0List,
    status: 0,
  };
}

/** 必要に応じて前回の結果で上書きする(階層1,2用) */
export function convertJudgement(
  beforeJudgement: number,
  afterJudgement: number,
) {
  // 今回がN/A、もしくは空の場合に前回の結果で上書きする
  return isNa(afterJudgement) || isEmpty(afterJudgement)
    ? beforeJudgement
    : afterJudgement;
}

/**
 * 前回の結果で上書きする
 * 階層0は階層1,2の上書き後の結果を見て判定する
 */
export function convertDepth0Judgement(
  beforeJudgement: number,
  afterJudgement: number,
  convertedDepth1List: GetInspectionItem[],
) {
  // 今回がOKかNGなら上書き処理不要
  if (isOk(afterJudgement) || isNg(afterJudgement)) {
    return afterJudgement;
  }
  // 階層1が無ければ前回の結果で上書きする
  if (convertedDepth1List.length === 0) {
    return beforeJudgement;
  }
  // 前回の階層0の結果より、上書き後の階層1の結果に応じた判定を優先する(マトリクス表示のパターンに対応)
  // 上書き後の階層1に未入力があれば階層0も未入力扱いにする
  if (convertedDepth1List.some((depth1) => isEmpty(depth1.judgement))) {
    return JUDGEMENT.EMPTY;
  }
  // 上書き後の階層1にNGがあれば階層0もNG扱いにする
  if (convertedDepth1List.some((depth1) => isNg(depth1.judgement))) {
    return JUDGEMENT.NG;
  }
  // 階層1がOKとN/Aのみで構成されている場合は、階層1にOKがあれば階層0もOK扱いにする
  if (convertedDepth1List.some((depth1) => isOk(depth1.judgement))) {
    return JUDGEMENT.OK;
  }
  // 前回も今回も全てN/Aの場合
  return JUDGEMENT.NA;
}

/** 数値を指定の小数点以下の桁数に丸める */
export function roundValue(numericalValue: number, digits: number) {
  const adjustValue = Math.pow(10, digits);
  return Math.round(numericalValue * adjustValue) / adjustValue;
}

/** 指定した整数部・小数部の桁数にする */
export function formatNumberValue(
  numericalValue: string,
  integer: number,
  decimal: number,
) {
  let formattedValue = numericalValue;
  if (numericalValue.includes('.')) {
    // 小数点前後で桁数数える
    const period = numericalValue.indexOf('.');
    formattedValue =
      numericalValue.substring(0, period).substring(0, integer) +
      '.' +
      numericalValue.substring(period + 1).substring(0, decimal);
  } else if (numericalValue.length > integer) {
    formattedValue = numericalValue.substring(0, integer);
  }
  return formattedValue;
}

/** 診断の確定状態を解除する時、ステータスと出荷検査回数を更新する */
export function getStatusAndQcCountOnRevert(
  status: number,
  qcCount: number,
): [number, number] {
  const { QC_COMPLETE, QC_IN_PROGRESS, INSPECTION_IN_PROGRESS } =
    INSPECTION_STATUSES;
  if (status === QC_COMPLETE) {
    return [QC_IN_PROGRESS, qcCount];
  }
  return [
    status >= QC_IN_PROGRESS && qcCount > 1
      ? QC_IN_PROGRESS
      : INSPECTION_IN_PROGRESS,
    status >= QC_IN_PROGRESS && qcCount > 1 ? qcCount - 1 : 0,
  ];
}

/** 数値入力でafterに値がなかったらbeforeの値で上書きする */
function getNumericalValue(
  before: GetInspectionItem,
  after: GetInspectionItem,
) {
  return after.numericalValue === null
    ? before.numericalValue
    : after.numericalValue;
}

/** 帳票に表示するようにデータを整形 */
export function createReportTable(report: GetReport, qcCount: number) {
  // 受入診断の結果で初期化
  const reportTable = insertInspectionResult(report, qcCount);
  // 出荷検査のみ存在またはNGの項目を追加
  report.qcResultList.forEach((category) => {
    const targetCategory = reportTable.find(
      (c) => c.categoryId === category.categoryId,
    );
    const categoryData: CategoryResultTable = targetCategory ?? {
      categoryId: category.categoryId,
      label: category.label,
      typeResultList: [],
    };
    category.typeResultList.forEach((type) => {
      const targetType = categoryData.typeResultList.find(
        (t) => t.typeId === type.typeId,
      );
      const typeData: TypeResultTable = targetType ?? {
        typeId: type.typeId,
        label: type.label,
        itemResultList: [],
      };

      type.itemResultList.forEach((item) => {
        addQcNumericalValue(typeData.itemResultList, item);
        // 0層目
        const targetDepth0 = typeData.itemResultList.find(
          (i) => i.itemId === item.itemId,
        );
        const depth0: ItemResultTable = targetDepth0
          ? {
              ...targetDepth0,
              inResult: getItemResult(item.itemId, report.inspectionResultList),
              qcResult: JUDGEMENT_LABEL[item.judgement],
            }
          : {
              itemId: item.itemId,
              label: item.label,
              inResult: getItemResult(item.itemId, report.inspectionResultList),
              qcResult: JUDGEMENT_LABEL[item.judgement],
              qcNumericalValue: item.numericalValue,
              itemResultList: [],
            };

        // 高度可変測定は差分の結果のみ表示
        if (
          type.typeId === VARIABLE_HARDNESS.typeId &&
          item.itemId !== VARIABLE_HARDNESS.itemId
        ) {
          return;
        }

        // 0層目は結果に依らず格納(表示)する
        if (!isExistItem(item.itemId, reportTable)) {
          typeData.itemResultList.push(depth0);
        }

        // NGの項目のみ下層を格納していく
        if (!isNg(item.judgement) || !item.itemResultList) {
          return;
        }

        item.itemResultList.forEach((problem) => {
          if (!isNg(problem.judgement)) {
            return;
          }
          const targetDepth1 = depth0.itemResultList.find(
            (p) => p.itemId === problem.itemId,
          );
          const depth1: ItemResultTable = targetDepth1
            ? {
                ...targetDepth1,
                inResult: getItemResult(
                  problem.itemId,
                  report.inspectionResultList,
                ),
                qcResult: JUDGEMENT_LABEL[problem.judgement],
              }
            : {
                itemId: problem.itemId,
                label: problem.label,
                inResult: getItemResult(
                  problem.itemId,
                  report.inspectionResultList,
                ),
                qcResult: JUDGEMENT_LABEL[problem.judgement],
                itemResultList: [],
              };
          // 2層目の項目が存在しない場合
          if (!problem.itemResultList) {
            depth0.itemResultList.push(depth1);
            return;
          }
          // 2層目の項目が存在する場合
          problem.itemResultList.forEach((detail) => {
            if (isNg(detail.judgement)) {
              const targetDepth2 = depth1.itemResultList.find(
                (d) => d.itemId === detail.itemId,
              );
              const depth2 = targetDepth2
                ? {
                    ...targetDepth2,
                    inResult: getItemResult(
                      detail.itemId,
                      report.inspectionResultList,
                    ),
                    qcResult: JUDGEMENT_LABEL[detail.judgement],
                  }
                : {
                    itemId: detail.itemId,
                    label: detail.label,
                    inResult: getItemResult(
                      detail.itemId,
                      report.inspectionResultList,
                    ),
                    qcResult: JUDGEMENT_LABEL[detail.judgement],
                    itemResultList: [],
                  };
              //  2層目がNGのものだけ格納
              if (!isExistItem(detail.itemId, reportTable)) {
                depth1.itemResultList.push(depth2);
              }
            }
          });
          if (!isExistItem(problem.itemId, reportTable)) {
            // 2層目を含んだ結果を格納
            depth0.itemResultList.push(depth1);
          }
        });
      });
      // 並び替え
      typeData.itemResultList.sort(
        (prev, next) => getIdIndex(prev.itemId) - getIdIndex(next.itemId),
      );
      // 中項目の結果を格納
      if (!targetType && typeData.itemResultList.length > 0) {
        categoryData.typeResultList.push(typeData);
      }
    });

    // 並び替え
    categoryData.typeResultList.sort(
      (prev, next) => getIdIndex(prev.typeId) - getIdIndex(next.typeId),
    );
    // 大項目の結果を格納
    if (!targetCategory && categoryData.typeResultList.length > 0) {
      reportTable.push(categoryData);
    }
  });

  reportTable.sort(
    (prev, next) => Number(prev.categoryId) - Number(next.categoryId),
  );

  return reportTable;
}

/** 帳票のテーブル表示用データに受入診断の結果を格納 */
function insertInspectionResult(report: GetReport, qcCount: number) {
  const inspectionReportTable: CategoryResultTable[] = [];

  report.inspectionResultList.forEach((category) => {
    const categoryData: CategoryResultTable = {
      categoryId: category.categoryId,
      label: category.label,
      typeResultList: [],
    };
    category.typeResultList.forEach((type) => {
      const typeData: TypeResultTable = {
        typeId: type.typeId,
        label: type.label,
        itemResultList: [],
      };

      type.itemResultList.forEach((item) => {
        // 0層目
        const depth0: ItemResultTable = {
          itemId: item.itemId,
          label: item.label,
          inResult: JUDGEMENT_LABEL[item.judgement],
          inNumericalValue: item.numericalValue,
          qcResult:
            qcCount === 0
              ? ''
              : getItemResult(item.itemId, report.qcResultList),
          itemResultList: [],
        };

        // 高度可変測定は差分の結果のみ表示
        if (
          type.typeId === VARIABLE_HARDNESS.typeId &&
          item.itemId !== VARIABLE_HARDNESS.itemId
        ) {
          return;
        }

        // 0層目は結果に依らず格納(表示)する
        typeData.itemResultList.push(depth0);

        // NGの項目のみ下層を格納していく
        if (!isNg(item.judgement) || !item.itemResultList) {
          return;
        }

        item.itemResultList.forEach((problem) => {
          if (!isNg(problem.judgement)) {
            return;
          }
          const depth1: ItemResultTable = {
            itemId: problem.itemId,
            label: problem.label,
            inResult: JUDGEMENT_LABEL[problem.judgement], // NG
            qcResult:
              qcCount === 0
                ? ''
                : getItemResult(problem.itemId, report.qcResultList),
            itemResultList: [],
          };
          // 2層目の項目が存在しない場合
          if (!problem.itemResultList) {
            depth0.itemResultList.push(depth1);
            return;
          }
          // 2層目の項目が存在する場合
          problem.itemResultList.forEach((detail) => {
            if (isNg(detail.judgement)) {
              const depth2 = {
                itemId: detail.itemId,
                label: detail.label,
                inResult: JUDGEMENT_LABEL[detail.judgement],
                qcResult:
                  qcCount === 0
                    ? ''
                    : getItemResult(detail.itemId, report.qcResultList),
                itemResultList: [],
              };
              //  2層目がNGのものだけ格納
              depth1.itemResultList.push(depth2);
            }
          });

          // 2層目を含んだ結果を格納
          depth0.itemResultList.push(depth1);
        });
      });
      // 中項目の結果を格納
      categoryData.typeResultList.push(typeData);
    });
    // 大項目の結果を格納
    inspectionReportTable.push(categoryData);
  });
  return inspectionReportTable;
}

/** itemIdで検索して帳票データに存在すれば結果を返す */
function getItemResult(itemId: string, target: CategoryResult[]) {
  const [categoryNum, typeNum] = itemId.split('-');
  const categoryId = categoryNum;
  const typeId = `${categoryNum}-${typeNum}`;

  let judgement: number | undefined;
  // 小分類を再帰的に探索する関数
  const searchItemResult = (itemResults: ItemResult[]): boolean => {
    return itemResults.some((item) => {
      if (item.itemId === itemId) {
        judgement = item.judgement;
        return true;
      }
      if (item.itemResultList) {
        return searchItemResult(item.itemResultList);
      }
      return false;
    });
  };

  target.some((category) => {
    if (category.categoryId !== categoryId) {
      return false;
    }
    return category.typeResultList.some((type) => {
      if (type.typeId !== typeId) {
        return false;
      }
      return searchItemResult(type.itemResultList);
    });
  });

  return judgement === undefined
    ? NOT_EXIST_RESULT_TEXT
    : JUDGEMENT_LABEL[judgement];
}

/** 帳票のテーブルデータの中に指定したIDの項目が存在するか */
function isExistItem(itemId: string, target: CategoryResultTable[]) {
  const [categoryNum, typeNum] = itemId.split('-');
  const categoryId = categoryNum;
  const typeId = `${categoryNum}-${typeNum}`;

  // 小分類を再帰的に探索する関数
  const searchItemResult = (itemResults: ItemResultTable[]): boolean => {
    return itemResults.some((item) => {
      if (item.itemId === itemId) {
        return true;
      }
      if (item.itemResultList.length > 0) {
        return searchItemResult(item.itemResultList);
      }
      return false;
    });
  };

  return target.some((category) => {
    if (category.categoryId !== categoryId) {
      return false;
    }
    return category.typeResultList.some((type) => {
      if (type.typeId !== typeId) {
        return false;
      }
      return searchItemResult(type.itemResultList);
    });
  });
}

/** 帳票に数値入力結果を表示する */
export function addQcNumericalValue(
  itemResultList: ItemResultTable[],
  newItem: ItemResult,
) {
  const index = itemResultList.findIndex(
    (item) => item.itemId === newItem.itemId,
  );
  if (index !== -1) {
    itemResultList[index] = {
      ...itemResultList[index],
      qcNumericalValue: newItem.numericalValue,
    };
  }
}

/** 帳票のテーブルに表示される行数を大項目ごとにカウント */
export function countTableRows(reportTable: CategoryResultTable[]) {
  const categoryRows: number[] = [];
  reportTable.forEach((category) => {
    // 大項目ごとの行数
    let categoryCount = 0;
    category.typeResultList.forEach((type) => {
      type.itemResultList.forEach((item) => {
        // 0層目まで
        if (item.itemResultList.length === 0) {
          categoryCount++;
          return;
        }
        item.itemResultList.forEach((problem) => {
          // 1層目まで
          if (problem.itemResultList.length === 0) {
            categoryCount++;
            return;
          }
          categoryCount += problem.itemResultList.length;
        });
      });
    });
    categoryRows.push(categoryCount);
  });
  // 先端部も含めて計算
  categoryRows.push(DTA_IMAGE_SIZE);
  return categoryRows;
}

/** 帳票を2列に分ける時のIndexを計算 */
export function getMidIndex(tableRowList: number[]): number {
  const sumRows = tableRowList.reduce(
    (accumulator, currentValue) => accumulator + currentValue,
    0,
  );
  const midRow = Math.ceil(sumRows / 2);
  const result = {
    tempSum: 0,
    index: 0,
  };
  for (let i = 0; i < tableRowList.length; i++) {
    result.tempSum += tableRowList[i];
    if (result.tempSum > midRow) {
      result.index = i;
      break;
    }
  }

  return result.index;
}

/** 帳票フォーマット用データの整形 */
export function createReportFormatTable(reportFormat: GetReportFormat) {
  const result = { inResult: '', qcResult: '' };
  // 表示する項目を格納する
  const reportTable: CategoryResultTable[] = [];

  reportFormat.resultList.forEach((category) => {
    const categoryData: CategoryResultTable = {
      categoryId: category.categoryId,
      label: category.label,
      typeResultList: [],
    };
    category.typeResultList.forEach((type) => {
      const targetType = categoryData.typeResultList.find(
        (t) => t.typeId === type.typeId,
      );
      const typeData: TypeResultTable = targetType ?? {
        typeId: type.typeId,
        label: type.label,
        itemResultList: [],
      };

      type.itemResultList.forEach((item) => {
        // 0層目
        const targetDepth0 = typeData.itemResultList.find(
          (i) => i.itemId === item.itemId,
        );
        const depth0: ItemResultTable = targetDepth0
          ? {
              ...targetDepth0,
              inResult: result.inResult,
              qcResult: result.qcResult,
            }
          : {
              itemId: item.itemId,
              label: item.label,
              inResult: result.inResult,
              qcResult: result.qcResult,
              itemResultList: [],
            };

        typeData.itemResultList.push(depth0);

        item.itemResultList.forEach((problem) => {
          const targetDepth1 = depth0.itemResultList.find(
            (p) => p.itemId === problem.itemId,
          );
          const depth1: ItemResultTable = targetDepth1
            ? {
                ...targetDepth1,
                inResult: result.inResult,
                qcResult: result.qcResult,
              }
            : {
                itemId: problem.itemId,
                label: problem.label,
                inResult: result.inResult,
                qcResult: result.qcResult,
                itemResultList: [],
              };
          // 2層目の項目が存在しない場合
          if (!problem.itemResultList) {
            depth0.itemResultList.push(depth1);
            return;
          }
          // 2層目の項目が存在する場合
          problem.itemResultList.forEach((detail) => {
            const targetDepth2 = depth1.itemResultList.find(
              (d) => d.itemId === detail.itemId,
            );
            const depth2 = targetDepth2
              ? {
                  ...targetDepth2,
                  inResult: result.inResult,
                  qcResult: result.qcResult,
                }
              : {
                  itemId: detail.itemId,
                  label: detail.label,
                  inResult: result.inResult,
                  qcResult: result.qcResult,
                  itemResultList: [],
                };

            depth1.itemResultList.push(depth2);
          });
          depth0.itemResultList.push(depth1);
        });
      });
      // 並び替え
      typeData.itemResultList.sort(
        (prev, next) => getIdIndex(prev.itemId) - getIdIndex(next.itemId),
      );
      // 中項目の結果を格納
      if (!targetType && typeData.itemResultList.length > 0) {
        categoryData.typeResultList.push(typeData);
      }
    });

    // 並び替え
    categoryData.typeResultList.sort(
      (prev, next) => getIdIndex(prev.typeId) - getIdIndex(next.typeId),
    );
    // 大項目の結果を格納
    if (categoryData.typeResultList.length > 0) {
      reportTable.push(categoryData);
    }
  });

  return reportTable;
}

/** 検査完了ボタンの出し分け */
export function getStringBtnFinishTest(status: number): string {
  if (status < INSPECTION_STATUSES.INSPECTION_COMPLETE) {
    return 'BTN_FINISH_INSPECTION';
  }
  return 'BTN_FINISH_QC';
}

/** Rコード、部番のソート(昇順) */
export function getSortedParts(data: ConfirmParts[]): ConfirmParts[] {
  return [...data].sort((a, b) => {
    const rCodeSort = a.rCode.localeCompare(b.rCode);
    if (rCodeSort !== 0) {
      return rCodeSort;
    }
    return a.partsCode.localeCompare(b.partsCode);
  });
}

/** ExFMのURLを取得する */
export function getExFMUrl(repairRequest: GetRepairRequest): string {
  let path = '';
  switch (repairRequest.status) {
    case INSPECTION_STATUSES.NO_FAULT_FOUND:
      path = EXFM_PATH.DETAIL;
      break;
    case INSPECTION_STATUSES.INSPECTION_COMPLETE:
      path = EXFM_PATH.PARTS;
      break;
    case INSPECTION_STATUSES.QC_REPAIRING:
    case INSPECTION_STATUSES.QC_COMPLETE:
      path = EXFM_PATH.QC;
      break;
    default:
      // 修理依頼一覧
      return `/#${INSPEX_PATHNAME.RMA_SEARCH}`;
  }
  return `${repairRequest.exFMUrl}${path}?${EXFM_QUERY_PARAM.RMA_NO}=${repairRequest.rmaNo}`;
}
/**
 * 指定したIDの小項目を再帰的に探索して取得する
 * @param nodes　小項目の配列
 * @param targetId  取得したい小項目のID
 * @returns 取得した小項目、見つからなかった場合はundefined
 */
export function findInspectionItem(
  nodes: GetInspectionItem[],
  targetId: string,
): GetInspectionItem | undefined {
  for (const node of nodes) {
    // 現在のノードのIDが一致するかチェック
    if (node.id === targetId) {
      return node;
    }
    // 子要素（items）がある場合は再帰的に探索
    if (node.itemData.length > 0) {
      const result = findInspectionItem(node.itemData, targetId);
      if (result) {
        return result;
      }
    }
  }
  // 項目が見つからなかった場合
  return undefined;
}
